import { useState, type ReactNode } from 'react';
import { CaretIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { UnreadNotificationIcon } from '@rsos/components/capstone/UnreadNotificationIcon/UnreadNotificationIcon';
import styles from './QueueSection.module.css';

interface QueueSectionProps {
  title: string;
  count: number;
  /** Shows the unread dot in the header; cleared once everything is read. */
  hasUnread: boolean;
  defaultExpanded: boolean;
  children: ReactNode;
}

/**
 * Collapsible queue category section (Active / Error / Closed). Renders a header
 * bar with the section title, a total count, an unread dot, and a filled caret
 * that toggles the body. Expansion is local UI state, seeded from
 * `defaultExpanded`.
 */
function QueueSection({
  title,
  count,
  hasUnread,
  defaultExpanded,
  children,
}: QueueSectionProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <section
      className={styles.section}
      data-name={`queue-section-${title.toLowerCase()}`}
    >
      <button
        type="button"
        className={styles.header}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(prev => !prev)}
        data-name="queue-section-toggle"
      >
        <span
          className={`${styles.caret}${isExpanded ? '' : ` ${styles.caretCollapsed}`}`}
        >
          <CaretIcon color="primaryText" width={11} height={8} />
        </span>
        <Text
          color="primaryText"
          size="normal"
          fontWeight="semibold"
          textTransform="uppercase"
        >
          {title}
        </Text>
        <Text color="secondaryText" size="normal">
          &middot;
        </Text>
        <Text color="secondaryText" size="normal">
          {count}
        </Text>
        {hasUnread && (
          <span className={styles.unreadContainer}>
            <UnreadNotificationIcon
              position="center"
              data-testid="queue-section-unread-icon"
            />
          </span>
        )}
      </button>
      {isExpanded && <div className={styles.content}>{children}</div>}
    </section>
  );
}

export default QueueSection;
